export type InterpolationFunction = (values: number[], progress: number) => number;

export interface IPoint {
  x: number;
  y: number;
}

export interface ICircle {
  x: number;
  y: number;
  radius: number;
}

export interface ISize {
  width: number;
  height: number;
}

export interface IRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ISpace {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export const InterpolationMap: { [key: string]: InterpolationFunction } = {
  linear: linearInterpolation,
  bezier: bezierInterpolation,
  catmull: catmullRomInterpolation,
};

export function getCentroid(points: IPoint[]): IPoint {
  const result = points.reduce(
    (acc, val) => {
      acc.x += val.x;
      acc.y += val.y;
      return acc;
    },
    { x: 0, y: 0 },
  );
  result.x /= points.length;
  result.y /= points.length;
  return result;
}

export function deg2rad(deg) {
  return (deg / 180) * Math.PI;
}

export function rad2deg(rad) {
  return (rad * 180) / Math.PI;
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) {
    return min;
  } else if (max < value) {
    return max;
  } else {
    return value;
  }
}

export function lengthDirX(angle: number, length: number, rads: boolean): number {
  if (rads) {
    return Math.cos(angle) * length;
  } else {
    return Math.cos(deg2rad(angle)) * length;
  }
}

export function lengthDirY(angle: number, length: number, rads: boolean): number {
  if (rads) {
    return Math.sin(angle) * length;
  } else {
    return Math.sin(deg2rad(angle)) * length;
  }
}

export function linearInterpolation(values: number[], progress: number): number {
  const m: number = values.length - 1;
  const f: number = m * progress;
  const i: number = Math.floor(f);

  if (progress < 0) {
    return linear(values[0], values[1], f);
  }

  if (progress > 1) {
    return linear(values[m], values[m - 1], m - f);
  }

  return linear(values[i], values[i + 1 > m ? m : i + 1], f - i);
}

export function bezierInterpolation(values: number[], progress: number): number {
  let b: number = 0;
  const n: number = values.length - 1;

  for (let i: number = 0; i <= n; i++) {
    b += Math.pow(1 - progress, n - i) * Math.pow(progress, i) * values[i] * bernstein(n, i);
  }

  return b;
}

export function catmullRomInterpolation(v: number[], progress: number): number {
  const m: number = v.length - 1;
  let f: number = m * progress;
  let i: number = Math.floor(f);

  if (v[0] === v[m]) {
    if (progress < 0) {
      i = Math.floor((f = m * (1 + progress)));
    }

    return catmullRom(v[(i - 1 + m) % m], v[i], v[(i + 1) % m], v[(i + 2) % m], f - i);
  } else {
    if (progress < 0) {
      return v[0] - (catmullRom(v[0], v[0], v[1], v[1], -f) - v[0]);
    }

    if (progress > 1) {
      return v[m] - (catmullRom(v[m], v[m], v[m - 1], v[m - 1], f - m) - v[m]);
    }

    return catmullRom(v[i ? i - 1 : 0], v[i], v[m < i + 1 ? m : i + 1], v[m < i + 2 ? m : i + 2], f - i);
  }
}

export function bernstein(n: number, i: number): number {
  return factorial(n) / factorial(i) / factorial(n - i);
}

export function linear(p0: number, p1: number, progress: number): number {
  return (p1 - p0) * progress + p0;
}

export function factorial(value: number): number {
  if (value === 0) {
    return 1;
  }

  let res: number = value;
  while (--value) {
    res *= value;
  }

  return res;
}

export function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const v0: number = (p2 - p0) * 0.5;
  const v1: number = (p3 - p1) * 0.5;
  const t2: number = t * t;
  const t3: number = t * t2;
  return (2 * p1 - 2 * p2 + v0 + v1) * t3 + (-3 * p1 + 3 * p2 - 2 * v0 - v1) * t2 + v0 * t + p1;
}

export function getDistance(pointA: IPoint, pointB: IPoint) {
  const dx: number = pointA.x - pointB.x;
  const dy: number = pointA.y - pointB.y;

  return Math.sqrt(dx * dx + dy * dy);
}

export function distanceSq(pointA: IPoint, pointB: IPoint) {
  const dx: number = pointA.x - pointB.x;
  const dy: number = pointA.y - pointB.y;

  return dx * dx + dy * dy;
}

export function getAngle(pointA: IPoint, pointB: IPoint) {
  const dx: number = pointB.x - pointA.x;
  const dy: number = pointB.y - pointA.y;

  return Math.atan2(dy, dx);
}

export function getRandom(min: number, max: number, seed?: string): number {
  // const generator = new Rng(seed, true);
  const generate = Math.random;
  return Math.floor(generate() * (max - min + 1)) + min;
}

export function getRandomFloat(min: number, max: number): number {
  const interval = 0.001;
  const r = Math.floor((Math.random() * (max - min + interval)) / interval);
  return r * interval + min;
}

export function isInRange(num: number, min: number, max: number) {
  return num >= min && num <= max;
}

export function isRectangleContaining(x: number, y: number, rect: IRectangle): boolean {
  return x >= rect.x && x < rect.x + rect.width && y >= rect.y && y < rect.y + rect.height;
}

export function isRectangleContainingOther(rect: IRectangle, other: IRectangle): boolean {
  return (
    rect.x <= other.x &&
    rect.y <= other.y &&
    rect.x + rect.width >= other.x + other.width &&
    rect.y + rect.height >= other.y + other.height
  );
}
